import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Bot, CalendarClock, CircleCheckBig, ClipboardList, Filter, Inbox, LifeBuoy, MessageSquareMore, Search, Sparkles, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import UsersSubscribersPanel from "@/components/admin/UsersSubscribersPanel";
import {
  buildReferenceCode,
  FEEDBACK_STATUS_LABELS,
  feedbackTypeOptions,
  SUPPORT_STATUS_LABELS,
  supportCategoryOptions,
  supportPriorityOptions,
  type FeedbackStatus,
  type FeedbackType,
  type SupportCategory,
  type SupportStatus,
} from "@/lib/support-center";
import { cn } from "@/lib/utils";

type SupportTicketRow = {
  id: string;
  user_id: string;
  account_role: string;
  category: SupportCategory;
  priority: string;
  status: SupportStatus;
  subject: string;
  message: string;
  created_at: string;
  updated_at: string;
  page_path: string | null;
  course_id: string | null;
  lesson_id: string | null;
  module_key: string | null;
  attachment_urls: string[];
  metadata: Record<string, unknown> | null;
};

type SupportUpdateRow = {
  id: string;
  ticket_id: string;
  created_at: string;
  message: string | null;
  status: SupportStatus | null;
  update_type: string;
  visible_to_user: boolean;
};

type FeedbackRow = {
  id: string;
  user_id: string;
  account_role: string;
  feedback_type: FeedbackType;
  status: FeedbackStatus;
  subject: string;
  message: string;
  created_at: string;
  updated_at: string;
  rating: number | null;
  sentiment: number | null;
  importance_level: number | null;
  page_path: string | null;
  course_id: string | null;
  lesson_id: string | null;
  module_key: string | null;
  metadata: Record<string, unknown> | null;
};

type TicketSummaryRow = {
  ticket_id: string;
  summary: string;
  suggested_category: SupportCategory | null;
  suggested_priority: string | null;
  suggested_tags: string[];
  duplicate_group_key: string | null;
  trend_signals: Record<string, unknown> | null;
};

type FeedbackSummaryRow = {
  feedback_id: string;
  summary: string | null;
  tags: string[];
  suggested_status: FeedbackStatus | null;
  duplicate_group_key: string | null;
  importance_score: number | null;
  metadata: Record<string, unknown> | null;
};

type ContentSuggestionRow = {
  id: string;
  feedback_id: string;
  requested_topic: string;
  requested_course_category: string | null;
  rationale: string | null;
  status: FeedbackStatus;
};

type InternalNoteRow = {
  id: string;
  ticket_id: string | null;
  feedback_id: string | null;
  note: string;
  tags: string[];
  created_by: string;
  created_at: string;
};

type ProfileRow = {
  id: string;
  display_name: string | null;
};

const supportStatusStyles: Record<SupportStatus, string> = {
  submitted: "border-primary/30 bg-primary/10 text-primary",
  in_review: "border-accent/30 bg-accent/10 text-accent",
  waiting_on_user: "border-secondary/30 bg-secondary/10 text-secondary",
  resolved: "border-border/80 bg-card text-foreground",
};

const feedbackStatusStyles: Record<FeedbackStatus, string> = {
  new: "border-primary/30 bg-primary/10 text-primary",
  reviewed: "border-accent/30 bg-accent/10 text-accent",
  planned: "border-secondary/30 bg-secondary/10 text-secondary",
  completed: "border-border/80 bg-card text-foreground",
  dismissed: "border-border/70 bg-background/70 text-muted-foreground",
};

const applyTimeFilter = <T extends { created_at: string }>(rows: T[], windowKey: string) => {
  if (windowKey === "all") return rows;
  const days = Number(windowKey);
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return rows.filter((row) => new Date(row.created_at).getTime() >= cutoff);
};

const titleCase = (value: string) =>
  value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const AdminReviewDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminRole();

  const [tab, setTab] = useState("support");
  const [loading, setLoading] = useState(true);
  const [supportTickets, setSupportTickets] = useState<SupportTicketRow[]>([]);
  const [supportUpdates, setSupportUpdates] = useState<SupportUpdateRow[]>([]);
  const [feedbackRows, setFeedbackRows] = useState<FeedbackRow[]>([]);
  const [ticketSummaries, setTicketSummaries] = useState<Record<string, TicketSummaryRow>>({});
  const [feedbackSummaries, setFeedbackSummaries] = useState<Record<string, FeedbackSummaryRow>>({});
  const [contentSuggestions, setContentSuggestions] = useState<Record<string, ContentSuggestionRow>>({});
  const [internalNotes, setInternalNotes] = useState<InternalNoteRow[]>([]);
  const [profileMap, setProfileMap] = useState<Record<string, string>>({});
  const [selectedSupportId, setSelectedSupportId] = useState<string | null>(null);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(null);
  const [supportSearch, setSupportSearch] = useState("");
  const [feedbackSearch, setFeedbackSearch] = useState("");
  const [supportStatusFilter, setSupportStatusFilter] = useState<string>("all");
  const [supportCategoryFilter, setSupportCategoryFilter] = useState<string>("all");
  const [supportRoleFilter, setSupportRoleFilter] = useState<string>("all");
  const [supportTimeFilter, setSupportTimeFilter] = useState<string>("all");
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState<string>("all");
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState<string>("all");
  const [feedbackRoleFilter, setFeedbackRoleFilter] = useState<string>("all");
  const [feedbackTimeFilter, setFeedbackTimeFilter] = useState<string>("all");
  const [noteDraft, setNoteDraft] = useState("");
  const [tagDraft, setTagDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [runningAi, setRunningAi] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    const [
      supportResponse,
      supportUpdatesResponse,
      feedbackResponse,
      ticketSummariesResponse,
      feedbackSummariesResponse,
      suggestionResponse,
      notesResponse,
    ] = await Promise.all([
      supabase.from("support_tickets").select("*").order("created_at", { ascending: false }).limit(200),
      supabase.from("support_ticket_updates").select("*").order("created_at", { ascending: false }).limit(400),
      supabase.from("user_feedback").select("*").order("created_at", { ascending: false }).limit(200),
      supabase.from("ai_ticket_summaries").select("*"),
      supabase.from("ai_feedback_tags").select("*"),
      supabase.from("content_suggestions").select("*"),
      supabase.from("admin_internal_notes").select("*").order("created_at", { ascending: false }).limit(400),
    ]);

    if (supportResponse.error || supportUpdatesResponse.error || feedbackResponse.error || notesResponse.error) {
      setLoading(false);
      toast.error("Unable to load the admin review dashboard.");
      return;
    }

    const supportData = (supportResponse.data || []) as SupportTicketRow[];
    const feedbackData = (feedbackResponse.data || []) as FeedbackRow[];
    const profileIds = Array.from(new Set([
      ...supportData.map((row) => row.user_id),
      ...feedbackData.map((row) => row.user_id),
    ]));

    const { data: profiles } = profileIds.length > 0
      ? await supabase.from("profiles").select("id, display_name").in("id", profileIds)
      : { data: [] as ProfileRow[] };

    const summaryMap: Record<string, TicketSummaryRow> = {};
    ((ticketSummariesResponse.data || []) as TicketSummaryRow[]).forEach((summary) => {
      summaryMap[summary.ticket_id] = summary;
    });

    const feedbackSummaryMap: Record<string, FeedbackSummaryRow> = {};
    ((feedbackSummariesResponse.data || []) as FeedbackSummaryRow[]).forEach((summary) => {
      feedbackSummaryMap[summary.feedback_id] = summary;
    });

    const suggestionMap: Record<string, ContentSuggestionRow> = {};
    ((suggestionResponse.data || []) as ContentSuggestionRow[]).forEach((suggestion) => {
      suggestionMap[suggestion.feedback_id] = suggestion;
    });

    const nextProfileMap: Record<string, string> = {};
    ((profiles || []) as ProfileRow[]).forEach((profile) => {
      nextProfileMap[profile.id] = profile.display_name || "Unnamed user";
    });

    setSupportTickets(supportData);
    setSupportUpdates((supportUpdatesResponse.data || []) as SupportUpdateRow[]);
    setFeedbackRows(feedbackData);
    setTicketSummaries(summaryMap);
    setFeedbackSummaries(feedbackSummaryMap);
    setContentSuggestions(suggestionMap);
    setInternalNotes((notesResponse.data || []) as InternalNoteRow[]);
    setProfileMap(nextProfileMap);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!adminLoading && isAdmin) {
      void loadData();
    }
  }, [adminLoading, isAdmin, loadData]);

  const summaryCards = useMemo(() => {
    const openSupport = supportTickets.filter((ticket) => ticket.status !== "resolved").length;
    const newFeedback = feedbackRows.filter((item) => item.status === "new").length;
    const planned = feedbackRows.filter((item) => item.status === "planned").length;
    const resolvedThisWeek = supportTickets.filter((ticket) => ticket.status === "resolved" && new Date(ticket.updated_at).getTime() >= Date.now() - 7 * 24 * 60 * 60 * 1000).length;
    const categoryCounts = supportTickets.reduce<Record<string, number>>((acc, ticket) => {
      acc[ticket.category] = (acc[ticket.category] || 0) + 1;
      return acc;
    }, {});
    const mostReported = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0];

    return [
      { label: "Open Support Tickets", value: openSupport, icon: Inbox },
      { label: "New Feedback", value: newFeedback, icon: MessageSquareMore },
      { label: "Planned Requests", value: planned, icon: ClipboardList },
      { label: "Resolved This Week", value: resolvedThisWeek, icon: CircleCheckBig },
      { label: "Most Reported Issue", value: mostReported ? `${titleCase(mostReported[0])} (${mostReported[1]})` : "None", icon: Sparkles },
    ];
  }, [feedbackRows, supportTickets]);

  const filteredSupport = useMemo(() => {
    return applyTimeFilter(supportTickets, supportTimeFilter).filter((ticket) => {
      const searchText = `${ticket.subject} ${ticket.message} ${ticket.page_path || ""}`.toLowerCase();
      return (
        (supportStatusFilter === "all" || ticket.status === supportStatusFilter) &&
        (supportCategoryFilter === "all" || ticket.category === supportCategoryFilter) &&
        (supportRoleFilter === "all" || ticket.account_role === supportRoleFilter) &&
        (!supportSearch.trim() || searchText.includes(supportSearch.trim().toLowerCase()))
      );
    });
  }, [supportTickets, supportTimeFilter, supportStatusFilter, supportCategoryFilter, supportRoleFilter, supportSearch]);

  const filteredFeedback = useMemo(() => {
    return applyTimeFilter(feedbackRows, feedbackTimeFilter).filter((feedback) => {
      const searchText = `${feedback.subject} ${feedback.message} ${feedback.page_path || ""}`.toLowerCase();
      return (
        (feedbackStatusFilter === "all" || feedback.status === feedbackStatusFilter) &&
        (feedbackTypeFilter === "all" || feedback.feedback_type === feedbackTypeFilter) &&
        (feedbackRoleFilter === "all" || feedback.account_role === feedbackRoleFilter) &&
        (!feedbackSearch.trim() || searchText.includes(feedbackSearch.trim().toLowerCase()))
      );
    });
  }, [feedbackRows, feedbackTimeFilter, feedbackStatusFilter, feedbackTypeFilter, feedbackRoleFilter, feedbackSearch]);

  const selectedSupport = selectedSupportId ? supportTickets.find((ticket) => ticket.id === selectedSupportId) || null : null;
  const selectedSupportTimeline = selectedSupport ? supportUpdates.filter((update) => update.ticket_id === selectedSupport.id) : [];
  const selectedSupportNotes = selectedSupport ? internalNotes.filter((note) => note.ticket_id === selectedSupport.id) : [];
  const selectedSupportSummary = selectedSupport ? ticketSummaries[selectedSupport.id] : undefined;

  const selectedFeedback = selectedFeedbackId ? feedbackRows.find((feedback) => feedback.id === selectedFeedbackId) || null : null;
  const selectedFeedbackNotes = selectedFeedback ? internalNotes.filter((note) => note.feedback_id === selectedFeedback.id) : [];
  const selectedFeedbackSummary = selectedFeedback ? feedbackSummaries[selectedFeedback.id] : undefined;
  const selectedSuggestion = selectedFeedback ? contentSuggestions[selectedFeedback.id] : undefined;

  const updateSupportStatus = async (ticket: SupportTicketRow, nextStatus: SupportStatus) => {
    const { error } = await supabase.from("support_tickets").update({ status: nextStatus, updated_at: new Date().toISOString() }).eq("id", ticket.id);
    if (error) {
      toast.error(error.message);
      return;
    }

    await supabase.from("support_ticket_updates").insert({
      ticket_id: ticket.id,
      created_by: user?.id || null,
      update_type: "status_change",
      status: nextStatus,
      message: `Status updated to ${SUPPORT_STATUS_LABELS[nextStatus]}.`,
      visible_to_user: true,
      metadata: { changed_from: ticket.status, changed_to: nextStatus },
    });

    toast.success("Support status updated.");
    await loadData();
  };

  const updateFeedbackStatus = async (feedback: FeedbackRow, nextStatus: FeedbackStatus) => {
    const { error } = await supabase.from("user_feedback").update({ status: nextStatus, updated_at: new Date().toISOString() }).eq("id", feedback.id);
    if (error) {
      toast.error(error.message);
      return;
    }

    if (contentSuggestions[feedback.id]) {
      await supabase.from("content_suggestions").update({ status: nextStatus, updated_at: new Date().toISOString() }).eq("feedback_id", feedback.id);
    }

    await supabase.from("feedback_status_history").insert({
      feedback_id: feedback.id,
      content_suggestion_id: contentSuggestions[feedback.id]?.id || null,
      previous_status: feedback.status,
      new_status: nextStatus,
      note: `Status updated to ${FEEDBACK_STATUS_LABELS[nextStatus]}.`,
      changed_by: user?.id || null,
    });

    toast.success("Feedback status updated.");
    await loadData();
  };

  const saveInternalNote = async () => {
    if (!user || !noteDraft.trim()) return;
    setSavingNote(true);

    const tags = tagDraft
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);

    const payload = {
      created_by: user.id,
      note: noteDraft.trim(),
      tags,
      ticket_id: selectedSupport?.id || null,
      feedback_id: selectedFeedback?.id || null,
      content_suggestion_id: selectedSuggestion?.id || null,
    };

    const { error } = await supabase.from("admin_internal_notes").insert(payload);
    setSavingNote(false);
    if (error) {
      toast.error(error.message);
      return;
    }

    setNoteDraft("");
    setTagDraft("");
    toast.success("Internal note saved.");
    await loadData();
  };

  const runAiTriage = async (entityType: "support_ticket" | "feedback", id: string) => {
    setRunningAi(id);
    const { error } = await supabase.functions.invoke("support-feedback-intelligence", {
      body: { entityType, id },
    });
    setRunningAi(null);

    if (error) {
      toast.error(error.message);
      return;
    }

    toast.success("AI triage updated.");
    await loadData();
  };

  if (adminLoading || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="rounded-full border-2 border-primary border-t-transparent h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="glass-card max-w-lg p-8 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground">Admin access required</h1>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">This dashboard is restricted to internal HeirWise admins.</p>
          <Button className="mt-5" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="container mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Internal operations</p>
              <h1 className="font-heading text-xl font-bold text-foreground">Admin Review Dashboard</h1>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate("/admin/upload")}>Open Media Manager</Button>
        </div>
      </header>

      <main className="container mx-auto max-w-7xl space-y-6 px-4 py-6">
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="glass-card p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{card.label}</p>
                  <div className="grid h-10 w-10 place-items-center rounded-2xl border border-primary/30 bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                </div>
                <p className="font-heading text-2xl font-bold text-foreground break-words">{card.value}</p>
              </div>
            );
          })}
        </section>

        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="border border-border bg-card/70 flex-wrap">
            <TabsTrigger value="support">Support tickets</TabsTrigger>
            <TabsTrigger value="feedback">Feedback queue</TabsTrigger>
            <TabsTrigger value="users" className="gap-1"><Users className="w-3 h-3" /> Users & Subscribers</TabsTrigger>
          </TabsList>

          <TabsContent value="support" className="space-y-5">
            <div className="glass-card p-5">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                <div className="xl:col-span-2">
                  <Label className="mb-2 block">Search</Label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input value={supportSearch} onChange={(event) => setSupportSearch(event.target.value)} placeholder="Search subject, message, or page" className="pl-10" />
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Status</Label>
                  <Select value={supportStatusFilter} onValueChange={setSupportStatusFilter}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {Object.entries(SUPPORT_STATUS_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Category</Label>
                  <Select value={supportCategoryFilter} onValueChange={setSupportCategoryFilter}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All categories</SelectItem>
                      {supportCategoryOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Time window</Label>
                  <Select value={supportTimeFilter} onValueChange={setSupportTimeFilter}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All time</SelectItem>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              {filteredSupport.length === 0 ? (
                <div className="glass-card p-6 text-sm text-muted-foreground">No support tickets match these filters.</div>
              ) : (
                filteredSupport.map((ticket) => (
                  <button key={ticket.id} type="button" onClick={() => setSelectedSupportId(ticket.id)} className="glass-card p-5 text-left transition-transform duration-200 hover:-translate-y-0.5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-heading text-lg font-bold text-foreground">{ticket.subject}</p>
                          <span className={cn("rounded-full border px-2.5 py-1 text-[11px] font-medium", supportStatusStyles[ticket.status])}>{SUPPORT_STATUS_LABELS[ticket.status]}</span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{buildReferenceCode(ticket.id)} · {profileMap[ticket.user_id] || ticket.user_id.slice(0, 8)} · {new Date(ticket.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex flex-wrap gap-2 text-[11px]">
                        <span className="rounded-full border border-border/70 bg-background/60 px-2.5 py-1 text-muted-foreground">{supportCategoryOptions.find((option) => option.value === ticket.category)?.label || ticket.category}</span>
                        <span className="rounded-full border border-border/70 bg-background/60 px-2.5 py-1 text-muted-foreground">Role: {titleCase(ticket.account_role)}</span>
                        <span className="rounded-full border border-border/70 bg-background/60 px-2.5 py-1 text-muted-foreground">Priority: {titleCase(ticket.priority)}</span>
                        {ticket.attachment_urls.length > 0 && <span className="rounded-full border border-border/70 bg-background/60 px-2.5 py-1 text-muted-foreground">Attachments: {ticket.attachment_urls.length}</span>}
                      </div>
                    </div>
                    <p className="mt-4 line-clamp-2 text-sm leading-6 text-muted-foreground">{ticket.message}</p>
                  </button>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="feedback" className="space-y-5">
            <div className="glass-card p-5">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                <div className="xl:col-span-2">
                  <Label className="mb-2 block">Search</Label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input value={feedbackSearch} onChange={(event) => setFeedbackSearch(event.target.value)} placeholder="Search title, feedback text, or page" className="pl-10" />
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Status</Label>
                  <Select value={feedbackStatusFilter} onValueChange={setFeedbackStatusFilter}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {Object.entries(FEEDBACK_STATUS_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Type</Label>
                  <Select value={feedbackTypeFilter} onValueChange={setFeedbackTypeFilter}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      {feedbackTypeOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Time window</Label>
                  <Select value={feedbackTimeFilter} onValueChange={setFeedbackTimeFilter}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All time</SelectItem>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              {filteredFeedback.length === 0 ? (
                <div className="glass-card p-6 text-sm text-muted-foreground">No feedback items match these filters.</div>
              ) : (
                filteredFeedback.map((feedback) => (
                  <button key={feedback.id} type="button" onClick={() => setSelectedFeedbackId(feedback.id)} className="glass-card p-5 text-left transition-transform duration-200 hover:-translate-y-0.5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-heading text-lg font-bold text-foreground">{feedback.subject}</p>
                          <span className={cn("rounded-full border px-2.5 py-1 text-[11px] font-medium", feedbackStatusStyles[feedback.status])}>{FEEDBACK_STATUS_LABELS[feedback.status]}</span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{buildReferenceCode(feedback.id)} · {profileMap[feedback.user_id] || feedback.user_id.slice(0, 8)} · {new Date(feedback.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex flex-wrap gap-2 text-[11px]">
                        <span className="rounded-full border border-border/70 bg-background/60 px-2.5 py-1 text-muted-foreground">{feedbackTypeOptions.find((option) => option.value === feedback.feedback_type)?.label || feedback.feedback_type}</span>
                        <span className="rounded-full border border-border/70 bg-background/60 px-2.5 py-1 text-muted-foreground">Role: {titleCase(feedback.account_role)}</span>
                        {feedback.importance_level && <span className="rounded-full border border-border/70 bg-background/60 px-2.5 py-1 text-muted-foreground">Importance: {feedback.importance_level}/4</span>}
                      </div>
                    </div>
                    <p className="mt-4 line-clamp-2 text-sm leading-6 text-muted-foreground">{feedback.message}</p>
                  </button>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="users" className="space-y-5">
            <UsersSubscribersPanel />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={Boolean(selectedSupport)} onOpenChange={(open) => !open && setSelectedSupportId(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          {selectedSupport && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  <span>{selectedSupport.subject}</span>
                  <span className={cn("rounded-full border px-2.5 py-1 text-[11px] font-medium", supportStatusStyles[selectedSupport.status])}>{SUPPORT_STATUS_LABELS[selectedSupport.status]}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">Submitted by<br /><span className="text-foreground font-medium">{profileMap[selectedSupport.user_id] || selectedSupport.user_id}</span></div>
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">Category<br /><span className="text-foreground font-medium">{supportCategoryOptions.find((option) => option.value === selectedSupport.category)?.label || selectedSupport.category}</span></div>
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">Priority<br /><span className="text-foreground font-medium">{titleCase(selectedSupport.priority)}</span></div>
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">Context<br /><span className="text-foreground font-medium">{titleCase(selectedSupport.account_role)}</span></div>
                </div>

                <div className="rounded-2xl border border-border/70 bg-background/55 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Full message</p>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-foreground">{selectedSupport.message}</p>
                </div>

                <div className="grid gap-4 lg:grid-cols-[0.9fr,1.1fr]">
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-border/70 bg-background/55 p-4 space-y-3 text-sm">
                      <p className="font-semibold text-foreground">App context</p>
                      <p className="text-muted-foreground">Page: <span className="text-foreground">{selectedSupport.page_path || "Unknown"}</span></p>
                      <p className="text-muted-foreground">Course: <span className="text-foreground">{selectedSupport.course_id || "—"}</span></p>
                      <p className="text-muted-foreground">Lesson: <span className="text-foreground">{selectedSupport.lesson_id || "—"}</span></p>
                      <p className="text-muted-foreground">Module: <span className="text-foreground">{selectedSupport.module_key || "—"}</span></p>
                      <p className="text-muted-foreground">Attachments: <span className="text-foreground">{selectedSupport.attachment_urls.length}</span></p>
                    </div>

                    <div className="rounded-2xl border border-border/70 bg-background/55 p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-semibold text-foreground">Status management</p>
                        <Button size="sm" variant="outline" disabled={runningAi === selectedSupport.id} onClick={() => runAiTriage("support_ticket", selectedSupport.id)}>
                          <Bot className="mr-2 h-4 w-4" />
                          {runningAi === selectedSupport.id ? "Running..." : "Run AI triage"}
                        </Button>
                      </div>
                      <Select value={selectedSupport.status} onValueChange={(value) => updateSupportStatus(selectedSupport, value as SupportStatus)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(SUPPORT_STATUS_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedSupportSummary && (
                      <div className="rounded-2xl border border-primary/30 bg-primary/10 p-4 space-y-3">
                        <div className="flex items-center gap-2 text-foreground">
                          <Sparkles className="h-4 w-4 text-primary" />
                          <p className="font-semibold">AI triage</p>
                        </div>
                        <p className="text-sm leading-6 text-foreground">{selectedSupportSummary.summary}</p>
                        <div className="flex flex-wrap gap-2 text-[11px]">
                          {selectedSupportSummary.suggested_tags.map((tag) => <span key={tag} className="rounded-full border border-primary/20 bg-background/60 px-2.5 py-1 text-foreground">{tag}</span>)}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-2xl border border-border/70 bg-background/55 p-4">
                      <p className="font-semibold text-foreground">Status history</p>
                      <div className="mt-3 space-y-3">
                        {selectedSupportTimeline.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No status history yet.</p>
                        ) : (
                          selectedSupportTimeline.map((entry) => (
                            <div key={entry.id} className="rounded-2xl border border-border/60 bg-card/50 p-3">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-sm font-medium text-foreground">{titleCase(entry.update_type)}</p>
                                <p className="text-xs text-muted-foreground">{new Date(entry.created_at).toLocaleString()}</p>
                              </div>
                              <p className="mt-2 text-sm text-muted-foreground">{entry.message || (entry.status ? SUPPORT_STATUS_LABELS[entry.status] : "Update recorded")}</p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-border/70 bg-background/55 p-4 space-y-3">
                      <p className="font-semibold text-foreground">Internal notes</p>
                      <div className="space-y-3 max-h-[14rem] overflow-y-auto">
                        {selectedSupportNotes.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No internal notes yet.</p>
                        ) : (
                          selectedSupportNotes.map((note) => (
                            <div key={note.id} className="rounded-2xl border border-border/60 bg-card/50 p-3">
                              <p className="text-sm leading-6 text-foreground">{note.note}</p>
                              <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                                {note.tags.map((tag) => <span key={tag} className="rounded-full border border-border/70 bg-background/60 px-2 py-1 text-muted-foreground">{tag}</span>)}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <Textarea value={noteDraft} onChange={(event) => setNoteDraft(event.target.value)} placeholder="Add an internal note for this ticket" className="min-h-[120px]" />
                      <Input value={tagDraft} onChange={(event) => setTagDraft(event.target.value)} placeholder="Optional tags, comma separated" />
                      <Button onClick={saveInternalNote} disabled={savingNote || !noteDraft.trim()}>{savingNote ? "Saving..." : "Save note"}</Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(selectedFeedback)} onOpenChange={(open) => !open && setSelectedFeedbackId(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          {selectedFeedback && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  <span>{selectedFeedback.subject}</span>
                  <span className={cn("rounded-full border px-2.5 py-1 text-[11px] font-medium", feedbackStatusStyles[selectedFeedback.status])}>{FEEDBACK_STATUS_LABELS[selectedFeedback.status]}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">Submitted by<br /><span className="text-foreground font-medium">{profileMap[selectedFeedback.user_id] || selectedFeedback.user_id.slice(0, 8)}</span></div>
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">Type<br /><span className="text-foreground font-medium">{feedbackTypeOptions.find((option) => option.value === selectedFeedback.feedback_type)?.label || selectedFeedback.feedback_type}</span></div>
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">Importance<br /><span className="text-foreground font-medium">{selectedFeedback.importance_level ? `${selectedFeedback.importance_level}/4` : "—"}</span></div>
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">Role context<br /><span className="text-foreground font-medium">{titleCase(selectedFeedback.account_role)}</span></div>
                </div>

                <div className="rounded-2xl border border-border/70 bg-background/55 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Full message</p>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-foreground">{selectedFeedback.message}</p>
                </div>

                <div className="grid gap-4 lg:grid-cols-[0.9fr,1.1fr]">
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-border/70 bg-background/55 p-4 space-y-3 text-sm">
                      <p className="font-semibold text-foreground">Submission context</p>
                      <p className="text-muted-foreground">Page: <span className="text-foreground">{selectedFeedback.page_path || "Unknown"}</span></p>
                      <p className="text-muted-foreground">Course: <span className="text-foreground">{selectedFeedback.course_id || "—"}</span></p>
                      <p className="text-muted-foreground">Lesson: <span className="text-foreground">{selectedFeedback.lesson_id || "—"}</span></p>
                      <p className="text-muted-foreground">Module: <span className="text-foreground">{selectedFeedback.module_key || "—"}</span></p>
                      <p className="text-muted-foreground">Rating: <span className="text-foreground">{selectedFeedback.rating || "—"}</span></p>
                    </div>

                    <div className="rounded-2xl border border-border/70 bg-background/55 p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-semibold text-foreground">Status management</p>
                        <Button size="sm" variant="outline" disabled={runningAi === selectedFeedback.id} onClick={() => runAiTriage("feedback", selectedFeedback.id)}>
                          <Bot className="mr-2 h-4 w-4" />
                          {runningAi === selectedFeedback.id ? "Running..." : "Run AI triage"}
                        </Button>
                      </div>
                      <Select value={selectedFeedback.status} onValueChange={(value) => updateFeedbackStatus(selectedFeedback, value as FeedbackStatus)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(FEEDBACK_STATUS_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedSuggestion && (
                      <div className="rounded-2xl border border-secondary/30 bg-secondary/10 p-4 space-y-2">
                        <div className="flex items-center gap-2 text-foreground">
                          <CalendarClock className="h-4 w-4 text-secondary" />
                          <p className="font-semibold">Content suggestion</p>
                        </div>
                        <p className="text-sm text-foreground">{selectedSuggestion.requested_topic}</p>
                        <p className="text-xs text-muted-foreground">Status: {FEEDBACK_STATUS_LABELS[selectedSuggestion.status]}</p>
                      </div>
                    )}

                    {selectedFeedbackSummary && (
                      <div className="rounded-2xl border border-primary/30 bg-primary/10 p-4 space-y-3">
                        <div className="flex items-center gap-2 text-foreground">
                          <Sparkles className="h-4 w-4 text-primary" />
                          <p className="font-semibold">AI triage</p>
                        </div>
                        {selectedFeedbackSummary.summary && <p className="text-sm leading-6 text-foreground">{selectedFeedbackSummary.summary}</p>}
                        <div className="flex flex-wrap gap-2 text-[11px]">
                          {selectedFeedbackSummary.tags.map((tag) => <span key={tag} className="rounded-full border border-primary/20 bg-background/60 px-2.5 py-1 text-foreground">{tag}</span>)}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-2xl border border-border/70 bg-background/55 p-4 space-y-3">
                      <p className="font-semibold text-foreground">Internal notes</p>
                      <div className="space-y-3 max-h-[14rem] overflow-y-auto">
                        {selectedFeedbackNotes.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No internal notes yet.</p>
                        ) : (
                          selectedFeedbackNotes.map((note) => (
                            <div key={note.id} className="rounded-2xl border border-border/60 bg-card/50 p-3">
                              <p className="text-sm leading-6 text-foreground">{note.note}</p>
                              <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                                {note.tags.map((tag) => <span key={tag} className="rounded-full border border-border/70 bg-background/60 px-2 py-1 text-muted-foreground">{tag}</span>)}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <Textarea value={noteDraft} onChange={(event) => setNoteDraft(event.target.value)} placeholder="Add an internal note for this feedback item" className="min-h-[120px]" />
                      <Input value={tagDraft} onChange={(event) => setTagDraft(event.target.value)} placeholder="Optional tags, comma separated" />
                      <Button onClick={saveInternalNote} disabled={savingNote || !noteDraft.trim()}>{savingNote ? "Saving..." : "Save note"}</Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReviewDashboard;
